/**
 * BannerBlock Component
 * 
 * Displays promotional banners, announcements, or decorative images.
 * Supports images with text overlays and click actions.
 */

import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { BannerBlockProps } from '@/types/menuBuilder';
import { useTranslation } from 'react-i18next';

interface BannerBlockComponentProps {
  props: BannerBlockProps;
  isPreview?: boolean;
}

export const BannerBlock = memo(function BannerBlock({
  props,
  isPreview = false,
}: BannerBlockComponentProps) {
  const { t } = useTranslation();
  
  const {
    imageUrl,
    linkType,
    linkTarget,
    aspectRatio,
    overlayText,
    overlayPosition = 'bottom',
    overlayColor,
    overlayOpacity = 0.5,
    height,
    borderRadius = 'md',
  } = props;

  // Aspect ratio classes
  const aspectRatioClasses: Record<string, string> = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '3:2': 'aspect-[3/2]',
    '2:1': 'aspect-[2/1]',
    '1:1': 'aspect-square',
    '9:16': 'aspect-[9/16]',
    'auto': '',
  };

  // Overlay position classes
  const overlayPositionClasses = {
    top: 'items-start pt-4',
    center: 'items-center',
    bottom: 'items-end pb-4',
  };

  // Border radius classes
  const borderRadiusClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
  };

  // No image - show placeholder
  if (!imageUrl) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-muted/50 border-2 border-dashed',
          aspectRatio !== 'auto' ? aspectRatioClasses[aspectRatio] : 'h-48',
          borderRadiusClasses[borderRadius]
        )}
        style={{ height: height || undefined }}
      >
        {!isPreview && (
          <span className="text-muted-foreground">
            {t('menuBuilder.configureBanner', 'Add image in properties')}
          </span>
        )}
      </div>
    );
  }

  const handleClick = () => {
    if (linkType === 'external' && linkTarget) {
      window.open(linkTarget, '_blank');
    } else if (linkType === 'category' && linkTarget) {
      const element = document.getElementById(linkTarget);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (linkType === 'item' && linkTarget) {
      // Dispatch a custom event that the parent renderer can listen to
      // This allows the public menu to handle item opening without direct coupling
      const event = new CustomEvent('menu-item-click', { 
        detail: { itemId: linkTarget },
        bubbles: true 
      });
      document.dispatchEvent(event);
    }
  };

  const isAutoHeight = aspectRatio === 'auto';

  return (
    <div
      className={cn(
        'relative overflow-hidden cursor-pointer',
        !isAutoHeight && aspectRatioClasses[aspectRatio],
        borderRadiusClasses[borderRadius]
      )}
      style={{ height: height || undefined }}
      onClick={linkType !== 'none' ? handleClick : undefined}
    >
      {/* Background Image */}
      <img
        src={imageUrl}
        alt={overlayText || 'Banner'}
        className={cn(
          "w-full object-cover",
          isAutoHeight ? "h-auto" : "absolute inset-0 h-full"
        )}
      />

      {/* Graphical Arts */}
      {props.graphicalArts?.map((art) => (
        <div
          key={art.id}
          className="absolute pointer-events-none"
          style={{
            left: `${art.positionX}%`,
            top: `${art.positionY}%`,
            width: `${art.width}%`,
            transform: `translate(-50%, -50%) rotate(${art.rotation || 0}deg)`,
            opacity: art.opacity ?? 1,
            zIndex: 10,
          }}
        >
          <img src={art.imageUrl} alt="Art" className="w-full h-auto" />
        </div>
      ))}

      {/* Overlay for text */}
      {overlayText && (
        <>
          <div
            className="absolute inset-0"
            style={{ 
              backgroundColor: overlayColor || '#000000',
              opacity: overlayOpacity,
            }}
          />
          <div
            className={cn(
              'absolute inset-0 flex flex-col justify-center px-4',
              overlayPositionClasses[overlayPosition]
            )}
          >
            <p className="text-white font-semibold text-lg drop-shadow-lg">
              {overlayText}
            </p>
          </div>
        </>
      )}
    </div>
  );
});

export default BannerBlock;
